import { useEffect, useState } from 'react';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { AppLayout } from '@/components/layout/app-layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/patterns';
import { formatoNumero } from '@/lib/utils/formato-precio';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { CheckCircle, CheckSquare, XCircle, Edit3, Eye, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/lib/hooks/use-toast';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { useApprovalStore } from '../store/approval-store';
import { Approval } from '../types/approval.types';
import { formatCurrency, formatDateString, formatPercentage } from '@/lib/utils/format';
import { ApprovalStatus } from '@/types';
import { AvisoDeEnvio } from '../components/aviso-de-envio';
import { etiquetaDelPaso, votoDelPaso, siguientePaso, personasValidas } from '../utils/firmantes';

export function ApprovalsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const puedeVerElPricer = useAuthStore((s) => s.canSeeMenu('pricer-datec'));
  const {
    approvals,
    stats,
    isLoading,
    fetchApprovals,
    fetchStats,
    approveQuote,
    rejectQuote,
    requestAdjustment,
  } = useApprovalStore();

  const [selectedApproval, setSelectedApproval] = useState<Approval | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject' | 'adjust' | null>(null);
  const [comments, setComments] = useState('');
  const [suggestedPrice, setSuggestedPrice] = useState('');
  type StatusFilter = 'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED';
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  type TipoFilter = 'TODAS' | 'APROBACION' | 'REAPERTURA' | 'AUTOMATICAS';
  const [tipoFilter, setTipoFilter] = useState<TipoFilter>('TODAS');
  const toggleFilter = (f: StatusFilter) => setStatusFilter((prev) => (prev === f ? 'ALL' : f));

  // Las estadísticas sólo existen para quien puede aprobar (`GET /api/approvals/stats` pide `approve_quotes`): al
  // resto se le contestaría 403. Misma fuente que la insignia del menú. Quien no aprueba sigue viendo sus cotizaciones.
  const puedeAprobar = useAuthStore((s) => s.hasPermission('approve_quotes'));

  useEffect(() => {
    fetchApprovals();
  }, []);

  useEffect(() => {
    if (puedeAprobar) fetchStats();
  }, [puedeAprobar]);

  /*
    🔴 El cartel "Margen Op." / "Margen Bruto" se daba vuelta.

    Se decidía con una lista de cotizaciones del Pricer pedida aparte. Pero un SALESPERSON solo recibe LAS SUYAS, así que una cotización
    de otra persona no estaba en el mapa y el cartel decía "Margen Bruto" sobre
    un número que es el operativo. Justo en la pantalla donde alguien decide si
    aprueba o no.

    El prefijo `PD-` lo pone el backend (`generateCode`) y no depende de qué
    alcance a ver quien mira.
  */
  const esDelPricer = (code?: string) => !!code?.startsWith('PD-');

  /** Las reglas que también aplicaban y que el sistema descartó. */
  const reglasDescartadas = (a: Approval): Array<{ id: string; name: string }> => {
    const extra = (a as unknown as { reglasAdicionales?: Array<{ id: string; name: string }> })
      .reglasAdicionales;
    return Array.isArray(extra) ? extra : [];
  };

  type Firmante = { id: string; firstName: string; lastName: string };

  /**
   * Los firmantes que vienen de OTRAS reglas, no de la que gobierna.
   *
   * Cuando la empresa activa "que firmen todas las reglas que apliquen", el
   * backend suma las cadenas y devuelve la unión en `eligibleApprovers`. La
   * cadena de la regla ganadora es solo una parte: el resto son estos.
   * Sin mostrarlos, la pantalla diría que falta una firma cuando faltan dos.
   */
  const sumados = (a: Approval): Firmante[] => {
    const cadena = a.triggerRule?.approvers ?? [];
    if (cadena.length === 0) return [];
    // Solo si esta aprobación pide más firmas que las que da su propia cadena.
    // Leerlo de la configuración de hoy cambiaría el pasado.
    if ((a.requiredApprovers ?? 1) <= cadena.length) return [];
    const propios = new Set(cadena.map((ra) => ra.userId));
    const todos = personasValidas(
      (a as unknown as { eligibleApprovers?: Firmante[] }).eligibleApprovers
    );
    return todos.filter((u) => !propios.has(u.id));
  };

  /**
   * ¿Le toca firmar a quien está mirando? Lo dice el SERVIDOR en `permisos` (FP T13b), con la misma cuenta que
   * usa al firmar: turno de la cadena, lo propio (permiso de aprobar lo propio) y el permiso de aprobar.
   *
   * 🔴 La pantalla mostraba los botones de aprobar/rechazar/ajustar a cualquier aprobador, aunque la cadena fuera
   * de otra persona, y el servidor devolvía un 400. Antes lo deducía del cargo; ahora no deduce nada: sin el
   * bloque `permisos`, no se ofrece (falla cerrado).
   */
  const puedeFirmar = (a: Approval): boolean => !!a.permisos?.puedeFirmar;

  /**
   * Rechazar o pedir un ajuste. Igual que firmar, salvo con la cadena TRABADA (un paso que nadie puede firmar):
   * ahí quien tiene el permiso de destrabar tiene salida, porque frenar no necesita a ese firmante.
   */
  const puedeFrenar = (a: Approval): boolean => !!a.permisos?.puedeFrenar;

  /** Las personas que pueden decidir sin cadena de cargos: sin nulos ni filas sin id. */
  const elegiblesDe = (a: Approval) =>
    personasValidas<Firmante>(
      (a as unknown as { eligibleApprovers?: Firmante[] }).eligibleApprovers
    );

  /** A quién le toca ahora, para poder decirlo en el tooltip. */
  const aQuienLeToca = (a: Approval): string => {
    const cadena = a.triggerRule?.approvers ?? [];
    const siguiente = siguientePaso(cadena, a.votes);
    return siguiente ? etiquetaDelPaso(siguiente) : '';
  };

  /*
    Al hacer clic en una fila. El destino sale de lo que trae la propia aprobación
    (`pricerDatecQuoteId`), no de una lista aparte: quien llega por la firma no
    tiene la cotización en ninguna lista suya. Si el cargo de quien firma no tiene
    la pantalla «Cotizaciones Datec», la ficha le daría un 403: se queda acá, desde
    donde ya puede decidir.
  */
  const navigateToQuote = (approval: Approval) => {
    if (approval.pricerDatecQuoteId) {
      if (!puedeVerElPricer) {
        toast({
          title: 'Decidí desde esta pantalla',
          description:
            'Tu cargo no tiene acceso a Cotizaciones Datec, así que la ficha no se puede abrir. Podés aprobar, rechazar o pedir un ajuste desde esta lista.',
        });
        return;
      }
      navigate(`/pricer-datec/${approval.pricerDatecQuoteId}`);
    } else {
      navigate(`/quotes/${approval.quoteId}`);
    }
  };

  const esReapertura = (a: Approval) => a.tipo === 'REAPERTURA';

  /** Chip del tipo de pedido, para la cola y el historial. */
  const chipTipo = (a: Approval) =>
    esReapertura(a) ? (
      <span className="inline-flex items-center rounded-full border border-violet-300 bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-800 dark:border-violet-700 dark:bg-violet-950 dark:text-violet-200">
        Reapertura
      </span>
    ) : (
      <span className="inline-flex items-center rounded-full border border-info-borde bg-info-suave px-2 py-0.5 text-xs font-semibold text-info ">
        Aprobación
      </span>
    );

  // Las que el usuario puede decidir ahora van primero (el sort es estable).
  const pendingApprovals = approvals
    .filter((a) => a.status === ApprovalStatus.PENDING)
    .sort((a, b) => Number(puedeFirmar(b)) - Number(puedeFirmar(a)));
  const votoPropio = (a: Approval, accion: 'APPROVE' | 'REJECT') =>
    !!a.votes?.some((v) => v.userId === currentUserId && v.action === accion);
  const historyApprovals = approvals.filter((a) => {
    if (a.status === ApprovalStatus.PENDING) return false;
    // Las tarjetas cuentan lo TUYO: se filtra por el voto del usuario, no por el estado final.
    if (statusFilter === 'APPROVED' && !votoPropio(a, 'APPROVE')) return false;
    if (statusFilter === 'REJECTED' && !votoPropio(a, 'REJECT')) return false;
    if (tipoFilter === 'APROBACION') return !esReapertura(a);
    if (tipoFilter === 'REAPERTURA') return esReapertura(a);
    if (tipoFilter === 'AUTOMATICAS') return !!a.automatica;
    return true;
  });
  // Sólo sin ningún filtro se muestran las 10 últimas: con un filtro activo se
  // ve todo lo que coincide.
  const hayFiltro = statusFilter !== 'ALL' || tipoFilter !== 'TODAS';
  const visibleHistory = hayFiltro ? historyApprovals : historyApprovals.slice(0, 10);
  const filterLabel: Record<StatusFilter, string> = {
    ALL: '',
    PENDING: 'Te toca decidir',
    APPROVED: 'Aprobadas por vos',
    REJECTED: 'Rechazadas por vos',
  };
  const statCardClass = (f: StatusFilter) =>
    `text-left rounded-lg transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
      statusFilter === f ? 'ring-2 ring-primary shadow-md' : 'hover:shadow-md'
    }`;

  const handleAction = async () => {
    if (!selectedApproval || !actionType) return;

    try {
      if (actionType === 'approve') {
        await approveQuote(selectedApproval.id, comments);
        toast({ title: 'Cotización aprobada', variant: 'success' });
      } else if (actionType === 'reject') {
        if (!comments.trim()) {
          toast({
            title: 'Error',
            description: 'Debes proporcionar un motivo de rechazo',
            variant: 'destructive',
          });
          return;
        }
        await rejectQuote(selectedApproval.id, comments);
        toast({ title: 'Cotización rechazada', variant: 'success' });
      } else if (actionType === 'adjust') {
        const parsedPrice = parseFloat(suggestedPrice);
        if (isNaN(parsedPrice) || parsedPrice <= 0) {
          toast({
            title: 'Error',
            description: 'El precio sugerido debe ser un número mayor a 0',
            variant: 'destructive',
          });
          return;
        }
        if (!comments.trim()) {
          toast({
            title: 'Error',
            description: 'Debes proporcionar comentarios',
            variant: 'destructive',
          });
          return;
        }
        await requestAdjustment(selectedApproval.id, parsedPrice, comments);
        toast({ title: 'Ajuste solicitado', variant: 'success' });
      }

      setSelectedApproval(null);
      setActionType(null);
      setComments('');
      setSuggestedPrice('');
    } catch (error: any) {
      toast({
        title: 'Error',
        // Sin red salía «Network Error» en inglés (decisión 261 §7.5).
        description: mensajeDeError(error, 'No se pudo completar la acción. Probá de nuevo.'),
        variant: 'destructive',
      });
    }
  };

  const openActionDialog = (approval: Approval, action: 'approve' | 'reject' | 'adjust') => {
    setSelectedApproval(approval);
    setActionType(action);
    setComments('');
    setSuggestedPrice('');
  };

  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] px-4 py-8 md:px-8">
        <div className="space-y-6">
          {/* El encabezado de Cotiza (artefacto «Aprobaciones», 19-oct). */}
          <PageHeader
            modulo="cotiza"
            etiqueta="Cotiza"
            icono={CheckSquare}
            titulo="Aprobaciones"
            subtitulo="Cotizaciones que necesitan tu decisión: aprobar, rechazar o pedir un ajuste de precio."
          />

          {/* Stats */}
          {puedeAprobar && stats && (
            <div
              data-tour="approvals-stats"
              className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4"
            >
              <button
                type="button"
                onClick={() => toggleFilter('PENDING')}
                aria-pressed={statusFilter === 'PENDING'}
                title={statusFilter === 'PENDING' ? 'Quitar filtro' : `Filtrar: Te toca decidir`}
                className={statCardClass('PENDING')}
              >
                <Card className="h-full">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Te toca decidir</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-titulo text-[32px] font-semibold leading-tight text-alerta">
                      {stats.pending}
                    </p>
                  </CardContent>
                </Card>
              </button>
              <button
                type="button"
                onClick={() => toggleFilter('APPROVED')}
                aria-pressed={statusFilter === 'APPROVED'}
                title={statusFilter === 'APPROVED' ? 'Quitar filtro' : `Filtrar: Aprobadas por vos`}
                className={statCardClass('APPROVED')}
              >
                <Card className="h-full">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Aprobadas por vos</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-titulo text-[32px] font-semibold leading-tight text-ok">
                      {stats.approved}
                    </p>
                  </CardContent>
                </Card>
              </button>
              <button
                type="button"
                onClick={() => toggleFilter('REJECTED')}
                aria-pressed={statusFilter === 'REJECTED'}
                title={
                  statusFilter === 'REJECTED' ? 'Quitar filtro' : `Filtrar: Rechazadas por vos`
                }
                className={statCardClass('REJECTED')}
              >
                <Card className="h-full">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Rechazadas por vos</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-titulo text-[32px] font-semibold leading-tight text-bloqueo">
                      {stats.rejected}
                    </p>
                  </CardContent>
                </Card>
              </button>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Tu tiempo promedio</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="font-titulo text-[32px] font-semibold leading-tight">
                    {formatoNumero(stats.avgResolutionTimeHours, 1)} h
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Pending Approvals */}
          <Card
            data-tour="approvals-queue"
            className={
              pendingApprovals.length > 0
                ? 'border-alerta-borde ring-1 ring-alerta-borde'
                : undefined
            }
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {pendingApprovals.length > 0 && (
                  <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full bg-alerta" />
                )}
                Por decidir
                {pendingApprovals.length > 0 && (
                  <span className="font-normal text-muted-foreground">
                    · {pendingApprovals.length}
                  </span>
                )}
              </CardTitle>
              <CardDescription>En cola, esperan tu respuesta.</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Cargando...</p>
                </div>
              ) : pendingApprovals.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Nada por decidir. Estás al día.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    {/*
                      19-oct (maqueta): 8 columnas en lugar de 13. Con 13, «Pendiente de» y
                      el flujo quedaban escondidos detrás de «Acciones» y la fila medía el
                      triple. Tipo y nombre van con el código; quién pide, con el cliente; la
                      nota del solicitante, con la razón.
                    */}
                    <TableRow>
                      <TableHead>Cotización</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead className="min-w-[180px]">Justificación</TableHead>
                      <TableHead className="text-right">Precio</TableHead>
                      <TableHead className="text-right">Margen</TableHead>
                      <TableHead className="min-w-[180px]">Flujo</TableHead>
                      {/* D11: «Acciones» queda fija al borde derecho; con los avisos la fila ya no la manda fuera de pantalla. */}
                      <TableHead className="sticky right-0 z-10 bg-background text-right shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.2)]">
                        Acciones
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingApprovals.map((approval) => (
                      <TableRow key={approval.id}>
                        <TableCell className="min-w-[150px] max-w-[200px] align-top">
                          <button
                            type="button"
                            className="whitespace-nowrap font-mono font-semibold text-primary hover:underline cursor-pointer"
                            onClick={() => navigateToQuote(approval)}
                          >
                            {approval.quote?.code}
                          </button>
                          {approval.cadenaTrabada && (
                            <p role="alert" className="mt-1 max-w-[260px] text-xs text-destructive">
                              {approval.cadenaTrabada.mensaje}
                            </p>
                          )}
                          <div className="mt-1">{chipTipo(approval)}</div>
                          {(approval.quote as any)?.name && (
                            <p
                              className="mt-1 truncate text-xs text-muted-foreground"
                              title={(approval.quote as any)?.name}
                            >
                              {(approval.quote as any)?.name}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[180px] align-top">
                          <p className="font-medium">{approval.quote?.clientName}</p>
                          <p className="truncate text-xs text-muted-foreground">
                            {approval.quote?.clientEmail}
                          </p>
                          <p className="mt-1 whitespace-nowrap text-xs text-muted-foreground">
                            Pide {approval.requestedBy?.firstName} {approval.requestedBy?.lastName}
                          </p>
                          {/* La fecha va acá (antes era la columna «Solicitado»): la tabla entra en 1440 px. */}
                          <p className="whitespace-nowrap text-xs text-muted-foreground">
                            el {formatDateString(approval.requestedAt)}
                          </p>
                        </TableCell>
                        <TableCell className="min-w-[180px] max-w-[220px] align-top">
                          {approval.triggerReason ? (
                            <span className="text-xs text-alerta">{approval.triggerReason}</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              {esReapertura(approval) ? '—' : 'Margen'}
                            </span>
                          )}
                          {/*
                            🔴 Cuando aplica más de una regla, el sistema gobierna con
                            una sola y descarta las demás. Antes eso no dejaba rastro:
                            el aprobador de la regla descartada nunca se enteraba, y la
                            aprobación se veía completamente normal.
                          */}
                          {!esReapertura(approval) && (
                            <AvisoDeEnvio
                              plegado
                              avisos={(approval as { avisosDeEnvio?: unknown }).avisosDeEnvio}
                            />
                          )}
                          {!esReapertura(approval) && reglasDescartadas(approval).length > 0 && (
                            <div className="mt-1 flex items-start gap-1 text-[11px] text-alerta ">
                              <AlertTriangle className="w-3 h-3 mt-[2px] shrink-0" />
                              <span>
                                También aplicaba{reglasDescartadas(approval).length > 1 ? 'n' : ''}:{' '}
                                {reglasDescartadas(approval)
                                  .map((r) => `«${r.name}»`)
                                  .join(', ')}
                              </span>
                            </div>
                          )}
                          {(approval as any).requestNote && (
                            <div className="mt-2">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <p className="text-xs whitespace-pre-line line-clamp-3 cursor-help text-foreground/80">
                                    {(approval as any).requestNote}
                                  </p>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-md whitespace-pre-line">
                                  {(approval as any).requestNote}
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="align-top whitespace-nowrap text-right font-semibold tabular-nums">
                          {formatCurrency(
                            approval.quote?.finalPrice || 0,
                            approval.quote?.currency?.code ?? 'USD'
                          )}
                        </TableCell>
                        <TableCell data-tour="approvals-margin" className="align-top text-right">
                          {/* actualMargin ya viene en % (ej. 23.08). PD guarda margen operativo; cotizaciones estándar, margen bruto. */}
                          {/* FP-4 (M-1): sin «Ver costos y márgenes» el margen no llega: «—», nunca «0 %». */}
                          {approval.quote?.costosOcultos ||
                          typeof approval.quote?.actualMargin !== 'number' ? (
                            <span
                              className="font-semibold text-muted-foreground"
                              title="Tu cargo no ve costos ni márgenes"
                              data-testid="margen-oculto"
                            >
                              —
                            </span>
                          ) : (
                            <span className="font-semibold text-alerta">
                              {/*
                                Con un decimal cuando es bajo. Redondeado a entero,
                                un margen de 1,9% se lee "2%", que es justo el rango
                                donde el aprobador necesita ver la diferencia.
                              */}
                              {formatPercentage(
                                approval.quote.actualMargin,
                                Math.abs(approval.quote.actualMargin) < 10 ? 1 : 0
                              )}
                            </span>
                          )}
                          <p className="text-[11px] text-muted-foreground whitespace-nowrap">
                            {esDelPricer(approval.quote?.code) ? 'Markup' : 'Margen Bruto'}
                          </p>
                        </TableCell>
                        <TableCell className="min-w-[170px] max-w-[200px] align-top">
                          <p className="mb-1 text-xs text-muted-foreground">Pendiente de</p>
                          {(() => {
                            // Una reapertura puede juntar varias reglas (una por LOB) y
                            // cualquiera de sus firmantes la resuelve: son alternativas,
                            // no una cadena. Se muestran todos, unidos por «o».
                            if (esReapertura(approval)) {
                              const alternativos = personasValidas(
                                (approval as unknown as { eligibleApprovers?: Firmante[] })
                                  .eligibleApprovers
                              ).filter((u) => !approval.votes?.some((v) => v.userId === u.id));
                              if (alternativos.length === 0) {
                                return <span className="text-xs text-muted-foreground">—</span>;
                              }
                              return (
                                <div className="flex items-start gap-1.5 text-xs">
                                  <span className="inline-block w-2 h-2 mt-1 rounded-full bg-alerta animate-pulse flex-shrink-0" />
                                  <span className="font-medium text-alerta">
                                    {alternativos
                                      .map((u) => `${u.firstName} ${u.lastName}`)
                                      .join(' o ')}
                                  </span>
                                </div>
                              );
                            }
                            const approvers = approval.triggerRule?.approvers ?? [];
                            // Caso 1: hay flujo secuencial → mostramos el proximo (menor stepOrder sin voto)
                            //
                            // 🔴 Con "que firmen todas las reglas que apliquen", el
                            // backend suma las cadenas de las otras reglas y las
                            // devuelve en `eligibleApprovers`. Si acá solo mirásemos
                            // la cadena de la regla ganadora, la pantalla diría que
                            // falta una sola firma cuando en realidad faltan dos, y
                            // el segundo firmante no aparecería en ningún lado.
                            if (approvers.length > 0 && sumados(approval).length === 0) {
                              const next = siguientePaso(approvers, approval.votes);
                              if (!next) {
                                return <span className="text-xs text-muted-foreground">—</span>;
                              }
                              return (
                                <div className="flex items-center gap-1.5 text-xs">
                                  <span className="inline-block w-2 h-2 rounded-full bg-alerta animate-pulse" />
                                  <span className="font-medium text-alerta">
                                    {etiquetaDelPaso(next)}
                                  </span>
                                </div>
                              );
                            }
                            // Caso 2: sin trigger rule → usamos eligibleApprovers del backend
                            // (todos los usuarios con rol APPROVER/ADMIN del tenant).
                            const eligibles = personasValidas<Firmante>(
                              (approval as any).eligibleApprovers
                            );
                            if (eligibles.length === 0) {
                              return <span className="text-xs text-muted-foreground">—</span>;
                            }
                            // Filtramos quienes ya votaron
                            const sinVotar = eligibles.filter(
                              (u) => !approval.votes?.some((v) => v.userId === u.id)
                            );
                            // Nadie aprueba lo propio: quien pidió o creó la cotización no figura como
                            // pendiente (si fueran los únicos, se muestran igual para no dejarlo vacío).
                            const sinLosPropios = sinVotar.filter(
                              (u) =>
                                u.id !== approval.requestedBy?.id && u.id !== approval.creadorId
                            );
                            const pending = sinLosPropios.length > 0 ? sinLosPropios : sinVotar;
                            if (pending.length === 0) {
                              return <span className="text-xs text-muted-foreground">—</span>;
                            }
                            return (
                              <div className="flex items-start gap-1.5 text-xs">
                                <span className="inline-block w-2 h-2 mt-1 rounded-full bg-alerta animate-pulse flex-shrink-0" />
                                <div className="flex flex-col">
                                  <span className="text-[10px] text-muted-foreground">
                                    Cualquiera de:
                                  </span>
                                  <span className="font-medium text-alerta">
                                    {pending
                                      .slice(0, 3)
                                      .map((u) => `${u.firstName} ${u.lastName}`)
                                      .join(', ')}
                                    {pending.length > 3 && ` +${pending.length - 3}`}
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                          <div className="mt-2 border-t pt-2">
                            {!esReapertura(approval) &&
                            approval.triggerRule?.approvers &&
                            approval.triggerRule.approvers.length > 0 ? (
                              <div className="space-y-1">
                                {approval.triggerRule.approvers.map((ra, idx) => {
                                  const vote = votoDelPaso(ra, approval.votes);
                                  const isNext =
                                    !vote &&
                                    !approval
                                      .triggerRule!.approvers.slice(0, idx)
                                      .some((prev) => !votoDelPaso(prev, approval.votes));
                                  return (
                                    <div key={ra.id} className="flex items-center gap-2 text-xs">
                                      <span
                                        className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                                          vote
                                            ? 'bg-ok-suave text-ok'
                                            : isNext
                                              ? 'bg-alerta-suave text-alerta ring-2 ring-alerta'
                                              : 'bg-muted text-muted-foreground'
                                        }`}
                                      >
                                        {vote ? '✓' : idx + 1}
                                      </span>
                                      <span
                                        className={
                                          vote
                                            ? 'text-ok font-medium'
                                            : isNext
                                              ? 'text-alerta font-medium'
                                              : 'text-muted-foreground'
                                        }
                                      >
                                        {etiquetaDelPaso(ra)}
                                        {!ra.user && vote?.user
                                          ? ` · ${vote.user.firstName} ${vote.user.lastName}`
                                          : ''}
                                      </span>
                                      {vote?.comments && (
                                        <Tooltip>
                                          <TooltipTrigger>
                                            <span className="text-info cursor-help">💬</span>
                                          </TooltipTrigger>
                                          <TooltipContent className="max-w-xs">
                                            <p className="text-xs">{vote.comments}</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      )}
                                    </div>
                                  );
                                })}
                                {/*
                                  Los que se suman por las OTRAS reglas que también
                                  aplicaban. Van después de la cadena de la regla que
                                  gobierna, y marcados, porque no son parte de ella.
                                */}
                                {sumados(approval).map((u) => {
                                  const vote = approval.votes?.find((v) => v.userId === u.id);
                                  return (
                                    <div key={u.id} className="flex items-center gap-2 text-xs">
                                      <span
                                        className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                                          vote
                                            ? 'bg-ok-suave text-ok'
                                            : 'bg-alerta-suave text-alerta ring-2 ring-alerta'
                                        }`}
                                      >
                                        {vote ? '✓' : '+'}
                                      </span>
                                      <span
                                        className={
                                          vote ? 'text-ok font-medium' : 'text-alerta font-medium'
                                        }
                                        title="Se suma porque también aplicaba otra regla"
                                      >
                                        {u.firstName} {u.lastName}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : elegiblesDe(approval).length > 0 ? (
                              // Sin trigger rule: mostramos la lista de aprobadores elegibles
                              // (cualquiera de ellos puede resolver, no es secuencial).
                              <div className="space-y-0.5">
                                <p className="text-xs text-muted-foreground">
                                  {esReapertura(approval)
                                    ? 'Cualquiera de ellos puede decidir'
                                    : 'Cualquiera puede aprobar'}
                                </p>
                                {elegiblesDe(approval)
                                  .slice(0, 5)
                                  .map((u) => {
                                    const vote = approval.votes?.find((v) => v.userId === u.id);
                                    return (
                                      <div key={u.id} className="flex items-center gap-2 text-xs">
                                        <span
                                          className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-bold ${
                                            vote
                                              ? 'bg-ok-suave text-ok'
                                              : 'bg-muted text-muted-foreground'
                                          }`}
                                        >
                                          {vote ? '✓' : '○'}
                                        </span>
                                        <span
                                          className={
                                            vote ? 'text-ok font-medium' : 'text-muted-foreground'
                                          }
                                        >
                                          {u.firstName} {u.lastName}
                                        </span>
                                      </div>
                                    );
                                  })}
                                {elegiblesDe(approval).length > 5 && (
                                  <p className="text-[10px] text-muted-foreground">
                                    +{elegiblesDe(approval).length - 5} más
                                  </p>
                                )}
                              </div>
                            ) : (approval.requiredApprovers ?? 1) > 1 ? (
                              <div>
                                <span
                                  className={`text-sm font-semibold ${(approval.votes?.filter((v) => v.action === 'APPROVE').length ?? 0) >= (approval.requiredApprovers ?? 1) ? 'text-ok' : 'text-alerta'}`}
                                >
                                  {approval.votes?.filter((v) => v.action === 'APPROVE').length ??
                                    0}
                                  /{approval.requiredApprovers}
                                </span>
                                {approval.votes && approval.votes.length > 0 && (
                                  <div className="text-xs text-muted-foreground mt-0.5">
                                    {approval.votes
                                      .filter((v) => v.action === 'APPROVE')
                                      .map((v) => v.user?.firstName)
                                      .join(', ')}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">1 aprobador</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell
                          data-tour="approvals-actions"
                          className="sticky right-0 z-10 bg-background text-right shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.2)]"
                        >
                          <div className="flex justify-end gap-2">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => navigateToQuote(approval)}
                                  aria-label={`Ver la cotización ${approval.quote?.code ?? ''}`}
                                  className="text-primary hover:text-primary"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Ver detalle de la cotización</TooltipContent>
                            </Tooltip>
                            {(approval.requestedBy?.id === currentUserId ||
                              approval.creadorId === currentUserId) &&
                            !puedeFirmar(approval) ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-xs italic text-muted-foreground px-2">
                                    Cotización propia
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  No podés aprobar/rechazar tu propia cotización (la creaste o la
                                  enviaste vos) — debe resolverla otro aprobador
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => openActionDialog(approval, 'approve')}
                                      disabled={!puedeFirmar(approval)}
                                      aria-label={`${esReapertura(approval) ? 'Aceptar la reapertura de' : 'Aprobar'} la cotización ${approval.quote?.code ?? ''}`}
                                      className="text-ok hover:text-ok"
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {puedeFirmar(approval)
                                      ? 'Aprobar esta cotización'
                                      : aQuienLeToca(approval)
                                        ? `Todavía no te toca: la cadena está en ${aQuienLeToca(approval)}`
                                        : 'Ya registraste tu firma en esta cotización'}
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => openActionDialog(approval, 'reject')}
                                      disabled={!puedeFrenar(approval)}
                                      aria-label={`${esReapertura(approval) ? 'Rechazar la reapertura de' : 'Rechazar'} la cotización ${approval.quote?.code ?? ''}`}
                                      className="text-bloqueo hover:text-bloqueo"
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {puedeFirmar(approval)
                                      ? 'Rechazar con motivo obligatorio'
                                      : aQuienLeToca(approval)
                                        ? `Todavía no te toca: la cadena está en ${aQuienLeToca(approval)}`
                                        : 'Ya registraste tu firma en esta cotización'}
                                  </TooltipContent>
                                </Tooltip>
                                {!esReapertura(approval) && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => openActionDialog(approval, 'adjust')}
                                        disabled={!puedeFrenar(approval)}
                                        aria-label={`Pedir un ajuste de precio en ${approval.quote?.code ?? ''}`}
                                      >
                                        <Edit3 className="w-4 h-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Solicitar ajuste de precio al vendedor
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* History */}
          {statusFilter !== 'PENDING' && (
            <Card data-tour="approvals-history">
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle>
                  Historial
                  {statusFilter !== 'ALL' && (
                    <span className="ml-2 text-sm font-normal text-muted-foreground">
                      · {filterLabel[statusFilter]} ({historyApprovals.length})
                    </span>
                  )}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <select
                    aria-label="Filtrar el historial por tipo"
                    value={tipoFilter}
                    onChange={(e) => setTipoFilter(e.target.value as TipoFilter)}
                    className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  >
                    <option value="TODAS">Todas</option>
                    <option value="APROBACION">Aprobación</option>
                    <option value="REAPERTURA">Reapertura</option>
                    <option value="AUTOMATICAS">Automáticas</option>
                  </select>
                  {hayFiltro && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setStatusFilter('ALL');
                        setTipoFilter('TODAS');
                      }}
                    >
                      Limpiar filtro
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cotización</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Aprobador</TableHead>
                      <TableHead>Comentarios</TableHead>
                      <TableHead>Fecha</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleHistory.map((approval) => (
                      <TableRow key={approval.id}>
                        <TableCell>
                          <button
                            type="button"
                            className="whitespace-nowrap font-mono text-primary hover:underline cursor-pointer"
                            onClick={() => navigateToQuote(approval)}
                          >
                            {approval.quote?.code}
                          </button>
                        </TableCell>
                        <TableCell>{chipTipo(approval)}</TableCell>
                        <TableCell className="max-w-[200px]">
                          <p
                            className="text-sm truncate"
                            title={(approval.quote as any)?.name || ''}
                          >
                            {(approval.quote as any)?.name || (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </p>
                        </TableCell>
                        <TableCell>{approval.quote?.clientName}</TableCell>
                        <TableCell>
                          {approval.automatica ? (
                            <Badge variant="success">Aprobada automáticamente</Badge>
                          ) : (
                            <Badge
                              variant={
                                approval.status === ApprovalStatus.APPROVED
                                  ? 'success'
                                  : approval.status === ApprovalStatus.ADJUSTMENT_REQUESTED
                                    ? 'warning'
                                    : approval.status === ApprovalStatus.CANCELLED
                                      ? 'outline'
                                      : 'destructive'
                              }
                            >
                              {esReapertura(approval)
                                ? approval.status === ApprovalStatus.APPROVED
                                  ? 'Reabierta'
                                  : 'Reapertura rechazada'
                                : approval.status === ApprovalStatus.APPROVED
                                  ? 'Aprobada'
                                  : approval.status === ApprovalStatus.ADJUSTMENT_REQUESTED
                                    ? approval.retirada
                                      ? 'Retirada'
                                      : 'Ajuste Solicitado'
                                    : approval.status === ApprovalStatus.CANCELLED
                                      ? 'Cancelada'
                                      : 'Rechazada'}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {approval.automatica ? (
                            <span className="text-xs text-muted-foreground">— (sistema)</span>
                          ) : approval.votes && approval.votes.length > 0 ? (
                            <div className="space-y-0.5">
                              {approval.votes.map((v) => (
                                <div key={v.id} className="text-xs">
                                  <span className="font-medium">
                                    {v.user?.firstName} {v.user?.lastName}
                                  </span>
                                  <Badge
                                    variant={v.action === 'APPROVE' ? 'success' : 'destructive'}
                                    className="ml-1 text-[10px] px-1 py-0"
                                  >
                                    {v.action === 'APPROVE' ? 'Aprobó' : 'Rechazó'}
                                  </Badge>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              {approval.approvedBy?.firstName} {approval.approvedBy?.lastName}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {approval.votes && approval.votes.some((v) => v.comments) ? (
                            <div className="space-y-0.5 max-w-xs">
                              {approval.votes
                                .filter((v) => v.comments)
                                .map((v) => (
                                  <div key={v.id} className="text-xs text-muted-foreground">
                                    <span className="font-medium">{v.user?.firstName}:</span>{' '}
                                    {v.comments}
                                  </div>
                                ))}
                            </div>
                          ) : approval.comments ? (
                            <span className="text-xs text-muted-foreground">
                              {approval.comments}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {approval.resolvedAt && formatDateString(approval.resolvedAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Action Dialog */}
      <Dialog open={!!selectedApproval} onOpenChange={() => setSelectedApproval(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' &&
                (selectedApproval && esReapertura(selectedApproval)
                  ? 'Aceptar reapertura'
                  : 'Aprobar Cotización')}
              {actionType === 'reject' &&
                (selectedApproval && esReapertura(selectedApproval)
                  ? 'Rechazar reapertura'
                  : 'Rechazar Cotización')}
              {actionType === 'adjust' && 'Solicitar Ajuste'}
            </DialogTitle>
            <DialogDescription>
              Cotización: {selectedApproval?.quote?.code} - {selectedApproval?.quote?.clientName}
            </DialogDescription>
          </DialogHeader>

          {selectedApproval && !esReapertura(selectedApproval) && (
            <AvisoDeEnvio
              avisos={(selectedApproval as { avisosDeEnvio?: unknown }).avisosDeEnvio}
            />
          )}

          {selectedApproval && esReapertura(selectedApproval) && actionType !== 'adjust' && (
            <p className="text-sm text-muted-foreground">
              Si aceptás, se guarda la versión aprobada y vuelve al vendedor para editar. Si
              rechazás, sigue aprobada tal cual.
            </p>
          )}

          {actionType === 'approve' && selectedApproval?.vencida && (
            <div
              role="status"
              className="rounded-md border border-alerta/50 bg-alerta/10 px-3 py-2 text-sm"
            >
              <strong>
                Esta cotización venció
                {selectedApproval.venceEl ? ` el ${selectedApproval.venceEl}` : ''}.
              </strong>{' '}
              Si la aprobás, vale de nuevo desde hoy.
            </div>
          )}

          <div className="space-y-4">
            {/*
              Cuántas firmas faltan.

              🔴 Sin esto, el aprobador de una cotización que necesita dos firmas
              apretaba "Aprobar" y la cotización SEGUÍA pendiente, sin ninguna
              explicación. Parecía que el botón no había hecho nada. Y con
              "que firmen todas las reglas que apliquen" eso pasa más seguido,
              porque ahora hay cadenas de más de una persona.
            */}
            {actionType === 'approve' &&
              selectedApproval &&
              (() => {
                const faltan =
                  (selectedApproval.requiredApprovers ?? 1) -
                  (selectedApproval.votes?.filter((v) => v.action === 'APPROVE').length ?? 0);
                if (faltan <= 1) return null;
                const cadena = selectedApproval.triggerRule?.approvers ?? [];
                const sinFirmar = cadena.filter((ra) => !votoDelPaso(ra, selectedApproval.votes));
                // El paso que firma quien mira: el suyo por nombre o, si no figura por
                // nombre, el primer paso por cargo que falta (el que está en turno).
                const esSumado = sumados(selectedApproval).some((u) => u.id === currentUserId);
                const miPaso =
                  sinFirmar.find((ra) => ra.userId === currentUserId) ??
                  (esSumado ? undefined : sinFirmar.find((ra) => !ra.userId));
                const otros = [
                  ...sinFirmar.filter((ra) => ra !== miPaso).map(etiquetaDelPaso),
                  ...sumados(selectedApproval)
                    .filter(
                      (u) =>
                        u.id !== currentUserId &&
                        !selectedApproval.votes?.some((v) => v.userId === u.id)
                    )
                    .map((u) => `${u.firstName} ${u.lastName}`),
                ];
                return (
                  <div className="rounded-md border border-alerta/50 bg-alerta/10 px-3 py-2 text-xs space-y-1">
                    <p className="font-semibold">Con tu firma no alcanza</p>
                    <p className="text-muted-foreground">
                      Esta cotización necesita{' '}
                      <strong>{selectedApproval.requiredApprovers} firmas</strong>. Con la tuya va a
                      seguir pendiente
                      {otros.length > 0 ? ` de ${otros.join(' y ')}` : ''}.
                    </p>
                  </div>
                );
              })()}

            {actionType === 'adjust' && (
              <div className="space-y-2">
                <Label>Precio Sugerido *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={suggestedPrice}
                  onChange={(e) => setSuggestedPrice(e.target.value)}
                  placeholder="Nuevo precio sugerido"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>
                Comentarios {actionType === 'reject' || actionType === 'adjust' ? '*' : ''}
              </Label>
              <Textarea
                rows={4}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder={
                  selectedApproval && esReapertura(selectedApproval) && actionType !== 'adjust'
                    ? 'Comentario'
                    : actionType === 'approve'
                      ? 'Comentarios adicionales (opcional)'
                      : actionType === 'reject'
                        ? 'Motivo del rechazo'
                        : 'Justificación del ajuste sugerido'
                }
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSelectedApproval(null)}>
                Cancelar
              </Button>
              <Button
                onClick={handleAction}
                variant={
                  actionType === 'approve'
                    ? 'default'
                    : actionType === 'reject'
                      ? 'destructive'
                      : 'default'
                }
              >
                {actionType === 'approve' &&
                  (selectedApproval && esReapertura(selectedApproval)
                    ? 'Aceptar y reabrir'
                    : 'Aprobar')}
                {actionType === 'reject' &&
                  (selectedApproval && esReapertura(selectedApproval)
                    ? 'Rechazar pedido'
                    : 'Rechazar')}
                {actionType === 'adjust' && 'Solicitar Ajuste'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

export default ApprovalsPage;
